import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  IconButton,
  Button,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Share as ShareIcon,
} from '@mui/icons-material';
import { paiseToRupees } from '../../Utils/currencyConverter';

export interface TripData {
  id: string;
  title: string;
  location: string;
  duration: string;
  price: string;
  imageUrl: string;
  category: string;
  type: 'Trip' | 'Itinerary';
  status?: string;
  bookings?: number;
  date?: string;
  shareUrl?: string;
}

interface TripCardProps {
  trip: any
  variant: 'explore' | 'trips' | 'bookings' | 'reservations' | 'transaction';
  onEdit?: (trip: TripData) => void;
  onShare?: (trip: TripData) => void;
  onClick?: (trip: TripData) => void;
}

const TripCard: React.FC<TripCardProps> = ({
  trip,
  variant,
  onEdit,
  onShare,
  onClick,
}) => {
  const [shareAnchorEl, setShareAnchorEl] = useState<null | HTMLElement>(null);

  const handleCardClick = () => {
    if (onClick) {
      onClick(trip);
    }
  };

  const handleShareClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setShareAnchorEl(event.currentTarget);
  };

  const handleShareClose = () => {
    setShareAnchorEl(null);
  };

  const getShareUrl = () => {
    if (trip?.shareUrl) return trip.shareUrl;
    if (typeof window !== 'undefined') return window.location.href;
    return '';
  };

  const handleCopyLink = async (event: React.MouseEvent) => {
    event.stopPropagation();
    const shareUrl = getShareUrl();
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      onShare?.(trip);
    } catch (error) {
      console.error('Failed to copy link', error);
    } finally {
      handleShareClose();
    }
  };

  const handleWhatsAppShare = (event: React.MouseEvent) => {
    event.stopPropagation();
    const shareUrl = encodeURIComponent(getShareUrl());
    if (shareUrl && typeof window !== 'undefined') {
      window.open(`https://wa.me/?text=${shareUrl}`, '_blank', 'noopener,noreferrer');
      onShare?.(trip);
    }
    handleShareClose();
  };

  const handleEmailShare = (event: React.MouseEvent) => {
    event.stopPropagation();
    const shareUrl = getShareUrl();
    if (shareUrl && typeof window !== 'undefined') {
      window.location.href = `mailto:?subject=${encodeURIComponent(trip?.title ?? 'Check out this trip')}&body=${encodeURIComponent(shareUrl)}`;
      onShare?.(trip);
    }
    handleShareClose();
  };

  // Explore variant - Grid card layout
  if (variant === 'explore') {
    return (
      <Box
        sx={{
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
        }}
        onClick={handleCardClick}
      >
        <Box sx={{ position: 'relative' }}>
          <CardMedia
            component="img"
            image={trip.files[0].fileURL}
            alt={trip.title}
            sx={{
              borderRadius: '20px',
              width: '100%',
              height: { xs: '200px', sm: '250px', md: '300px', lg: '350px' },
              objectFit: 'cover',
              maxWidth: '100%',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              bgcolor: '#FFE0B2',
              color: '#8B4513',
              borderRadius: '0 20px 0 16px',
              px: 3,
              py: 1,
              fontSize: '14px',
              fontWeight: 500,
              boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
            }}
          >
            {trip?.type ? `${trip.type.charAt(0).toUpperCase()}${trip.type.slice(1)}` : ''}

          </Box>
          <Box
            sx={{
              position: 'absolute',
              right: 10,
              bottom: 10,
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
            }}
          >
            <Button
              variant="outlined"
              sx={{
                minWidth: 0,
                background: 'rgba(255, 255, 255, 0.25)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '2px solid rgba(255, 255, 255, 0.18)',
                color: '#49454F',
                borderRadius: '24px',
                height: 48,
                px: 3,
                fontSize: '14px',
                fontWeight: 500,
                boxShadow: '0 8px 32px rgba(31, 38, 135, 0.37)',
                textTransform: 'none',
                display: 'flex',
                alignItems: 'center',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.35)',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  boxShadow: '0 8px 32px rgba(31, 38, 135, 0.45)',
                  transform: 'translateY(-2px)',
                  transition: 'all 0.3s ease',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <Typography sx={{ color: '#49454F', fontWeight: 600, fontSize: '14px' }}>
                {trip.user.username}
              </Typography>
            </Button>
            <IconButton
              onClick={handleShareClick}
              sx={{
                background: 'rgba(255, 255, 255, 0.25)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '2px solid rgba(255, 255, 255, 0.18)',
                color: '#49454F',
                borderRadius: '24px',
                width: 48,
                height: 48,
                boxShadow: '0 8px 32px rgba(31, 38, 135, 0.37)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.35)',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  boxShadow: '0 8px 32px rgba(31, 38, 135, 0.45)',
                  transform: 'translateY(-2px)',
                  transition: 'all 0.3s ease',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <ShareIcon />
            </IconButton>
            <Menu
              anchorEl={shareAnchorEl}
              open={Boolean(shareAnchorEl)}
              onClose={handleShareClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  borderRadius: 3,
                  mt: 1,
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(12px)',
                  minWidth: 180,
                },
              }}
            >
              <MenuItem onClick={handleCopyLink}>Copy link</MenuItem>
              <MenuItem onClick={handleWhatsAppShare}>Share via WhatsApp</MenuItem>
              <MenuItem onClick={handleEmailShare}>Share via Email</MenuItem>
            </Menu>
          </Box>
        </Box>
        <CardContent sx={{ p: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5, fontSize: '16px' }}>
            {trip.title}
          </Typography>
          <Typography variant="body2" color="#008387" sx={{ mb: 1 }}>
            {trip?.days} days • ₹{paiseToRupees(trip?.pricePerPerson).toFixed(2)}/person
          </Typography>
        </CardContent>
      </Box>
    );
  }

  // Trips variant - List layout with actions
  if (variant === 'trips') {
    return (
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: 1,
          mb: 2,
          overflow: 'hidden',
          position: 'relative',
        }}
        onClick={handleCardClick}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
          <CardMedia
            component="img"
            sx={{ width: 100, height: 100, borderRadius: 2 }}
            image={trip.imageUrl}
            alt={trip.title}
          />
          <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold" >
                {trip.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {trip.duration} • {trip.price}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
              {trip.status === 'pending' || trip.status === 'rejected' ? (
                <Box
                  sx={{
                    background: 'linear-gradient(90deg, #b31217 0%, #e52d27 100%)',
                    color: '#fff',
                    borderRadius: 2,
                    px: 3,
                    py: 0.5,
                    fontSize: '14px',
                    fontWeight: 500,
                    alignSelf: 'flex-start',
                  }}
                >
                  {trip.status === 'pending' ? 'Pending' : 'Rejected'}
                </Box>
              ) : (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onShare?.(trip);
                  }}
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    width: 40,
                    height: 40,
                  }}
                >
                  <ShareIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            background: 'linear-gradient(90deg, #28c76f 0%, #00c57d 100%)',
            color: '#fff',
            borderRadius: '0 0 0 16px',
            px: 3,
            py: 1,
            fontSize: '14px',
            fontWeight: 500,
            boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
          }}
        >
          {trip.type}
        </Box>
      </Card>
    );
  }


  return null;
};

export default TripCard; 